import { Component, computed, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { UiButtonComponent, UIIconComponent, UiNumberComponent } from '@design-system';
import { MASKED_VALUE } from '../../../../models/masked-value.model';
import { WalletStore } from '../../data-access/wallet.store';
import {
	WALLET_BALANCE_COLORS,
	WALLET_BALANCE_FOLD_PADDING,
	WALLET_BALANCE_LABELS,
	WALLET_BALANCE_ORDER,
	WALLET_BALANCE_OTHERS_COLOR,
	WALLET_BALANCE_PRIMARY_KEYS
} from '../../domain/wallet-balance.constants';
import { WalletBalanceRow } from '../../domain/wallet-balance.model';

/**
 * موجودی کل بورس‌کالا — every wallet's مانده added up, and each wallet's share of it as
 * a bar under the figure.
 *
 * مانده rather than قدرت خرید throughout, per the handoff: money an open order has blocked
 * is still the customer's, so it counts toward the total and toward its wallet's slice.
 *
 * گواهی سپرده and اصلی each get a slice and a row; the rest share one «سایر» slice, and
 * a «سایر» row that opens the «سایر کیف‌پول‌ها» sheet. Each slice grows with its balance
 * from a minimum width, so an empty one still shows as a sliver rather than vanishing.
 */
@Component({
	selector: 'app-wallet-balance-status',
	imports: [UiButtonComponent, UIIconComponent, UiNumberComponent],
	templateUrl: './wallet-balance-status.component.html',
	host: { class: 'block w-full', 'data-cy': 'wallet-balance-status' }
})
export class WalletBalanceStatusComponent {
	private readonly walletStore = inject(WalletStore);

	readonly amountsHidden = input(false);
	/** Folds the bar and the legend away, leaving only the total. */
	readonly collapsed = input(false);
	/** «سایر» was pressed — the page opens the sheet that lists those wallets. */
	readonly otherWalletsRequested = output<void>();

	private readonly breakdown = viewChild.required<ElementRef<HTMLElement>>('breakdown');

	/**
	 * The height the card gives back when it folds — the breakdown and the padding it sheds
	 * going from `py-3` to `py-1.5`; zero once it already has.
	 */
	breakdownHeight(): number {
		return this.collapsed() ? 0 : this.breakdown().nativeElement.offsetHeight + WALLET_BALANCE_FOLD_PADDING;
	}

	readonly maskedValue = MASKED_VALUE;
	readonly othersColor = WALLET_BALANCE_OTHERS_COLOR;

	readonly totalBalance = this.walletStore.totalBalance;

	/** گواهی سپرده and اصلی, in the handoff's order. */
	readonly primaryRows = computed<WalletBalanceRow[]>(() =>
		WALLET_BALANCE_PRIMARY_KEYS.map(key => ({
			key,
			label: WALLET_BALANCE_LABELS[key],
			balance: this.walletStore.wallets()[key].balance,
			colorClass: WALLET_BALANCE_COLORS[key]
		}))
	);

	/** The primary wallets' slices, then every other wallet as one «سایر» slice. */
	readonly segments = computed<WalletBalanceRow[]>(() => [
		...this.primaryRows(),
		{
			key: 'Others',
			label: 'سایر',
			balance: WALLET_BALANCE_ORDER.filter(key => !WALLET_BALANCE_PRIMARY_KEYS.includes(key)).reduce(
				(total, key) => total + this.walletStore.wallets()[key].balance,
				0
			),
			colorClass: WALLET_BALANCE_OTHERS_COLOR
		}
	]);
}
